import { z } from 'zod';

import { isWireDay } from '../utils/date';
import { ScheduleEventType, ScheduleStatus } from '../enums';
import { assertPickedDayCoherent, PickedCivilDayDto } from './civilDate';

export const CreateScheduleNutritionDto = z
  .object({
    dateTime: z.coerce.date(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    eventType: z.enum([
      ScheduleEventType.NUTRITION_PLAN,
      ScheduleEventType.MEAL,
    ]),
    mealId: z.string().uuid().nullish(),
    nutritionPlanId: z.string().uuid().nullish(),
    trainerId: z.string().uuid(),
    // At least one target must be provided. `clientId`/`groupId` are the
    // single-target forms kept for back-compat; `clientIds`/`groupIds` let one
    // request fan out to several clients and/or groups (each group is expanded
    // to its active clients on the server, then the whole set is de-duped).
    clientId: z.string().uuid().optional(),
    clientIds: z.array(z.string().uuid()).optional(),
    groupId: z.string().uuid().optional().nullable(),
    groupIds: z.array(z.string().uuid()).optional(),
    organizationId: z.string().uuid().nullish(),
    comment: z.string().optional(),
    firstInCycle: z.boolean().optional(),
  })
  .strict()
  .refine(
    (data) =>
      Boolean(data.clientId) ||
      Boolean(data.groupId) ||
      Boolean(data.clientIds?.length) ||
      Boolean(data.groupIds?.length),
    {
      message: 'VALIDATION__Required',
      path: ['clientId'],
    },
  )
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  )
  // The content has to match the event type, and this is the chokepoint for
  // it. `schedule.nutrition.controller.create` gates the source-content check
  // on the PAIR (`NUTRITION_PLAN` + `nutritionPlanId`, else `MEAL` +
  // `mealId`), and duplicates the meal only for `MEAL`: a `NUTRITION_PLAN`
  // payload carrying just a `mealId` matched neither branch, so it reached the
  // write with the premium door AND the nutrition read gate skipped, storing
  // the library meal's id verbatim on the client's row.
  //
  // A VALUE, not a key: both frontend payloads are built by spreading one base
  // object, so the other kind's key can legitimately ride along as `null` —
  // which `.nullish()` accepts and which names no content.
  //
  // The meal-day rows of a validated plan are written by the service, after
  // the plan source is authorized, so they never pass through this DTO.
  .superRefine((data, ctx) => {
    const wantsPlan = data.eventType === ScheduleEventType.NUTRITION_PLAN;
    const named = wantsPlan ? data.nutritionPlanId : data.mealId;
    const foreign = wantsPlan ? data.mealId : data.nutritionPlanId;
    const namedPath = wantsPlan ? 'nutritionPlanId' : 'mealId';
    const foreignPath = wantsPlan ? 'mealId' : 'nutritionPlanId';

    if (!named) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__Required',
        path: [namedPath],
      });
    }
    if (foreign) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__NotAllowed',
        path: [foreignPath],
      });
    }
  });

export type ApiCreateScheduleNutritionDto = z.infer<
  typeof CreateScheduleNutritionDto
>;

export const UpdateScheduleNutritionDto = z
  .object({
    dateTime: z.coerce.date().optional(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    status: z
      .enum([
        ScheduleStatus.PENDING,
        ScheduleStatus.COMPLETED,
        ScheduleStatus.MISSED,
        ScheduleStatus.CANCELLED,
      ])
      .optional(),
    comment: z.string().optional(),
    firstInCycle: z.boolean().optional(),
  })
  .strict()
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiUpdateScheduleNutritionDto = z.infer<
  typeof UpdateScheduleNutritionDto
>;

export const ListScheduleNutritionQueryDto = z.object({
  // Optional: omitting it against the organizations/:organizationId mount
  // requests the org-wide (OWNER-only) listing instead of one client's rows.
  // The bare mount still refuses a request without it, in the handler.
  clientId: z.string().uuid().optional(),
  // Day-anchored scheduling: a bare `yyyy-MM-dd` pair filters the frozen
  // `businessDate` column; ISO instants keep the legacy dateTime window.
  from: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
  to: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
});

export type ApiListScheduleNutritionQueryDto = z.infer<
  typeof ListScheduleNutritionQueryDto
>;
